import PropTypes from 'prop-types';
import React from 'react';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faTableCells } from '@fortawesome/free-solid-svg-icons/faTableCells';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _findIndex from 'lodash/findIndex';
import _trim from 'lodash/trim';
import styled from 'styled-components';

import ButtonGroup from 'ravenjs/lib/ButtonGroup';
import Col from 'ravenjs/lib/Col';
import Container from 'ravenjs/lib/Container';
import FaIcon from 'ravenjs/lib/FaIcon';
import Pagination from 'ravenjs/lib/Pagination';
import Sorting from 'ravenjs/lib/Sorting';
import Button from 'ravenjs/lib/Button';
import Row from 'ravenjs/lib/Row';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { isReactComponent } from 'ravenjs/utils/component';
import { renderCheck } from 'ravenjs/utils/viewport';
import { warning } from 'ravenjs/utils/warning';

import DataFilters from 'ravenjs/components/ListFilters';
import DataViewFilter from 'ravenjs/components/DataViewFilter';
import DataList from 'ravenjs/components/DataViewNew/DataList';
import SearchBox from 'ravenjs/components/SearchBox';
import Toolbar from 'ravenjs/components/Toolbar';
import Tooltip from 'ravenjs/lib/Tooltip';
import DataGrid from 'ravenjs/components/DataGrid';
import Typography from 'ravenjs/lib/Typography';

import { FilterIcon } from 'components/Icons';

import { ADVANCED_SEARCH_TITLE } from 'constants/handbooks';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

const ToolbarRightButtonGroupStyled = styled(ButtonGroup)`
    ${({ theme }) => theme.media.down('md')`
        margin-top: 10px;
        width: 100%;
    `};
`;

const FilterButton = styled(Button)`
    &:focus {
        box-shadow: none;
    }
`;

const ResetFilters = styled(Typography)`
    ${({ theme }) => theme.media.down('md')`
        font-size: 0.675rem !important;
    `};
`;

function DataView(props) {
    const {
        actions,
        dataViewAds,
        data,
        dataFilters,
        schema,
        updateOnMount: updateOnMountProps,
        uiSchema,
        hideDataFilters,
        hasModalAdvancedSearch,
        hasSelectAdvancedSearch,
        hasActiveAdvancedFilters,
        resetPagination,
    } = props;
    // Extract the `onChange` ref from the schema.
    // Determine the `onChangeHandler`.
    const onChangeHandler = getHandler(actions, get(schema, 'onChangeRef'));
    const onChangeView = getHandler(actions, 'onClickViewChange');
    // Find the selected schema.
    const selectedSchema = get(schema, 'selected', {});
    // Find the actions schema.
    const actionsSchema = get(schema, 'actions', null);
    // Find the main filter schema.
    const mainFilterSchema = get(schema, 'mainFilter', null);
    // Find the extraProperties schema.
    const extraCssPropertiesSchema = get(schema, 'extraCssProperties', null);
    // Are we rendering list filters?
    const listFiltersAvailable = !isEmpty(dataFilters) && !hideDataFilters;
    // Fetch Advance Search from the schema
    const advancedSearch = get(schema, 'search.advancedSearch', []);
    // Fetch sort options from the schema
    const sortingOptions = get(schema, 'sorting.options', {});
    // `use`Hooks.
    // State
    const [dataViewFilters, setDataViewFilters] = React.useState(
        get(schema, 'dataView.filters', null)
    );
    const currentSearchSpec = get(schema, 'search.value', null);
    const padding = get(uiSchema, 'padding', '0 5px');
    const showScroll = get(schema, 'showScroll', false);
    const width = get(schema, 'width', 'auto');
    const scrollStyles = get(schema, 'scrollStyles', false);
    const forceRemovePadding = get(schema, 'forceRemovePadding', false);
    const description = get(schema, 'description', null);
    const descriptionColor = get(schema, 'descriptionColor', null);
    const [order, setOrder] = React.useState(get(schema, 'sorting.default.order', ''));
    const [orderBy, setOrderBy] = React.useState(get(schema, 'sorting.default.orderBy', ''));
    const [extraCss, setExtraCss] = React.useState(null);
    const [page, setPage] = React.useState(get(schema, 'pagination.startPage', 1));
    const [rowsPerPage, setRowsPerPage] = React.useState(get(schema, 'pagination.rowsPerPage', 5));
    const [searchSpec, setSearchSpec] = React.useState(currentSearchSpec);
    const [advancedSearchSpec, setAdvanceSearchSpec] = React.useState(advancedSearch);
    const [advancedSearchStringValue, setAdvancedSearchStringValue] = React.useState('');
    const [defaultSortOption, setDefaultSortOption] = React.useState(
        get(schema, 'sorting.defaultOptions')
    );
    const [view, setView] = React.useState(get(schema, 'view.default', 'list'));

    React.useEffect(() => {
        if (resetPagination) {
            const onResetPaginationFinish = getHandler(actions, 'onResetPaginationFinish');
            setPage(1);
            callFunc(onResetPaginationFinish);
        }
    }, [actions, resetPagination]);

    // Refs
    const updateOnMount = React.useRef(updateOnMountProps);
    // Effects
    React.useEffect(
        () => {
            // Only run this effect based on the `updateOnMount` props.
            // NOTE: This way we can pass in a prop `updateOnMount={false}` and the
            // component will not run the `onChangeHandler` during the mounting phase.
            if (updateOnMount.current) {
                // Put all the values of the hooks together as the `currentState`.
                const currentState = {
                    dataFilters,
                    dataViewFilters,
                    order,
                    orderBy,
                    page,
                    rowsPerPage,
                    advancedSearchSpec,
                    searchSpec: _trim(searchSpec).length === 0 ? null : _trim(searchSpec),
                    totalRows: get(schema, 'pagination.totalRows', 0),
                };
                // If the `onChangeHandler` is present, call it with entire current state.
                callFunc(onChangeHandler, currentState);
            } else {
                updateOnMount.current = true;
            }
        },
        // Only initiate the hook when these properties change.
        [
            dataFilters,
            onChangeHandler,
            order,
            orderBy,
            page,
            rowsPerPage,
            schema,
            searchSpec,
            advancedSearchSpec,
            dataViewFilters,
        ]
    );

    React.useEffect(() => {
        if (hasModalAdvancedSearch && currentSearchSpec !== searchSpec) {
            setSearchSpec(currentSearchSpec);
        }
    }, [schema, currentSearchSpec, searchSpec, hasModalAdvancedSearch]);

    if (isEmpty(schema)) {
        warning(!isEmpty(schema), [
            `A <DataView /> requires a 'schema' prop to build the Data List Table.`,
        ]);
        return null;
    }

    const {
        filters = {},
        headerCellStyle = {},
        list = {},
        pagination = {},
        refs = {},
        search = {},
        sorting = {},
        toolbar = {},
        view: viewProps = {},
        searchStyleProps = {},
    } = schema;

    const { position = null } = pagination;
    const paginationPositionBottom = position === 'bottom' || position === 'both';
    const paginationPositionTop = position === 'top' || position === 'both';
    const hideToolbar = get(toolbar, 'hideToolbar');
    const toolbarProps = get(toolbar, 'props');
    const searchBoxStyleProps = get(searchStyleProps, 'backgroundColor', '');

    function onClickResetFilters() {
        const onClickFetchHandbooksDefault = getHandler(actions, 'onClickFetchHandbooksDefault');

        setSearchSpec('');
        callFunc(onClickFetchHandbooksDefault);
    }

    function onClickAdvanceSearchFilter() {
        const onClickAdvanceSearchFilterCallback = getHandler(
            actions,
            'onClickAdvanceSearchFilter',
            null
        );
        callFunc(onClickAdvanceSearchFilterCallback);
    }

    // Handle the `onCancel` for Search.
    function handleSearchOnCancel(e) {
        e.preventDefault();
        const onClearSearchListCallback = getHandler(actions, 'onClearSearchList');
        callFunc(onClearSearchListCallback);
        setSearchSpec('');
        const clearMenuInputs = advancedSearchSpec.map(menu => {
            return {
                ...menu,
                value: '',
            };
        });
        setAdvanceSearchSpec(clearMenuInputs);
        setPage(1);
    }

    // handle advance search clear search button
    function handleAdvancedSearchOnCancel(advanceSearchValue) {
        if (isEmpty(advanceSearchValue)) {
            setAdvancedSearchStringValue(advanceSearchValue);
        }
    }
    // Handle the `onChange` for Search.
    function handleSearchOnChange(val, advanceSearchMenuItems) {
        const onSearchChangeCallback = getHandler(actions, 'onSearchChange');
        callFunc(onSearchChangeCallback, val);
        val === '' && setSearchSpec(val);
    }

    // Handle the `onChange` for AdvanceSearch.
    function handleAdvancedSearchOnChange(val, advanceSearchMenuItems) {
        setSearchSpec(val);
        if (advanceSearchMenuItems && Array.isArray(advanceSearchMenuItems)) {
            setAdvanceSearchSpec(advanceSearchMenuItems);
        }
    }
    // Handle the `onClick` for Search.
    function handleSearchOnClick(val) {
        val && setSearchSpec(val);
    }
    // Handle the `onKeyPress` for Search.
    function handleSearchOnKeyPress(e) {
        // If we are pressing the 'Enter' key,
        // let's update the search spec.
        // (e.key === 'Enter') === (charCode === 13)
        if (e.charCode === 13) {
            e.preventDefault();
            e.target.blur();

            setSearchSpec(e.target.value || '');
            const onSearchKeyPressCallback = getHandler(actions, 'onSearchKeyPress');
            callFunc(onSearchKeyPressCallback, e.target.value);

            if (isEmpty(advancedSearchStringValue)) {
                const clearMenuInputs = advancedSearchSpec.map(menu => {
                    return {
                        ...menu,
                        value: '',
                    };
                });
                setAdvanceSearchSpec(clearMenuInputs);
            }
            setPage(1);
        }
    }
    // Handle the `onSortChange` event.
    function handleSortChange(currentOrder, newOrderBy) {
        const newOrder = currentOrder && currentOrder === 'asc' ? 'desc' : 'asc';
        if (newOrder && orderBy) {
            setOrder(orderBy === newOrderBy ? newOrder : 'asc');
            setOrderBy(newOrderBy);
            const index = _findIndex(sortingOptions, { orderBy: newOrderBy });
            setDefaultSortOption(sortingOptions[index]);
            setPage(1);
        }
    }
    // Handle the `onChangePage` event.
    function handlePageOnChange(e, newPage) {
        setPage(newPage);
    }
    // Handle the `onChangeRowsPerPage` event.
    function handleRowsPerPageOnChange({ value: newRowsPerPage }) {
        setRowsPerPage(newRowsPerPage);
    }
    // Handle the `apply Main Filter` event.
    function handleDataViewFiltersOnClick(filters) {
        const buildFilters = !isEmpty(dataViewFilters) ? _cloneDeep(dataViewFilters) : {};
        filters.map(option => {
            buildFilters[option.label] = option.value;
            return option;
        });
        setDataViewFilters(buildFilters);
        setPage(1);
    }
    // Handle the `extraCss` event.
    function handleExtraCss(status) {
        setExtraCss(status);
    }

    function onClearFilters() {
        const onClearFiltersCallback = getHandler(actions, 'onClearFilters');
        callFunc(onClearFiltersCallback);
        setPage(1);
    }

    function onSelectedFilterRemove(filter) {
        const onSelectedFilterRemoveCallback = getHandler(actions, 'onSelectedFilterRemove');
        callFunc(onSelectedFilterRemoveCallback, filter);
        setPage(1);
    }

    // Render the `LEFT` of the `Toolbar`
    function renderToolbarLeft() {
        const { left: Left } = toolbar;
        // Check if we are overriding the `LEFT` Component of the `Toolbar`.
        if (isReactComponent(Left)) {
            return <Left />;
        }

        if (!isEmpty(mainFilterSchema)) {
            const DataViewFilterHandlers = {
                ...actions,
                handleDataViewFiltersOnClick,
            };
            const { options, placeholderButtonLabel, ...rest } = mainFilterSchema;
            const { id: idFilter } = rest;
            const mainFilter = (
                <DataViewFilter
                    handlers={DataViewFilterHandlers}
                    placeholderButtonLabel={placeholderButtonLabel}
                    options={options}
                    type="main"
                    defaultValue={dataViewFilters ? dataViewFilters[idFilter] : []}
                    {...rest}
                />
            );
            return mainFilter;
        }

        // Carry on if we're not overriding...
        const { switcher = {} } = viewProps;
        // Don't render if the switcher schema is empty.
        if (isEmpty(switcher)) {
            return null;
        }
        // Extract the button texts.
        const { text: btnTexts = {}, ...restSwitcherSchema } = switcher;
        // Build the handlers list for the `ButtonGroup`.
        const handlers = {
            switchToGridView: () => {
                setView('grid');
                onChangeView && onChangeView('grid');
            },
            switchToListView: () => {
                setView('list');
                onChangeView && onChangeView('list');
            },
        };
        // Build some `IconProps` for the `ButtonGroup` icons.
        const IconProps = {
            width: '16px',
            height: '16px',
        };
        // Build the buttons list for the `ButtonGroup`.
        const buttons = [
            {
                id: 'grid',
                icon: FaIcon,
                label: btnTexts.grid || 'Tile View',
                onClick: 'switchToGridView',
                IconProps: {
                    ...IconProps,
                    icon: faTableCells,
                },
            },
            {
                id: 'list',
                icon: FaIcon,
                label: btnTexts.list || 'List View',
                onClick: 'switchToListView',
                IconProps: {
                    ...IconProps,
                    icon: faBars,
                },
            },
        ];
        // Render the `ButtonGroup`
        return (
            <ButtonGroup
                {...restSwitcherSchema}
                buttons={buttons}
                handlers={handlers}
                activeId={view}
                toggle
            />
        );
    }
    // Render the `CENTER` of the `Toolbar`
    function renderToolbarCenter() {
        const { center: Center } = toolbar;
        //
        const { placeholder = '' } = search;
        const isMobile = renderCheck('md', 'less');
        // Build the props for the Search.
        const searchProps = {
            onCancel: handleSearchOnCancel,
            onChange: handleSearchOnChange,
            onClick: handleSearchOnClick,
            onKeyPress: handleSearchOnKeyPress,
            placeholder,
            value: searchSpec,
            onChangeAdvancedSearch: handleAdvancedSearchOnChange,
            advancedSearch: advancedSearchSpec,
            advancedSearchSelect: hasSelectAdvancedSearch,
            onAdvancedSearchCancel: handleAdvancedSearchOnCancel,
            searchStyleProps,
        };
        // Check if we are overriding the `CENTER` Component of the `Toolbar`.
        if (isReactComponent(Center)) {
            return <Center {...searchProps} />;
        }
        // Carry on if we're not overriding...
        return (
            !isEmpty(search) && (
                <Row
                    justify="center"
                    gutter={false}
                    style={{ alignItems: 'center', flexWrap: 'nowrap' }}
                >
                    <SearchBox {...searchProps} />
                    {hasModalAdvancedSearch && (
                        <>
                            <Tooltip
                                content={ADVANCED_SEARCH_TITLE}
                                padding="4px 16px"
                                margin={0}
                                arrowSize={5}
                                placement="top"
                                borderRadius="5px"
                            >
                                <FilterButton
                                    color="white"
                                    noMinWidth
                                    noMinHeight
                                    height="100%"
                                    padding="3px 10px"
                                    margin="0 0 0 -42px"
                                    borderRadius="0.25rem"
                                    onClick={onClickAdvanceSearchFilter}
                                >
                                    <FilterIcon
                                        width="20px"
                                        height="20px"
                                        fill={
                                            hasActiveAdvancedFilters
                                                ? `${MIGRATED_COLORS.orange}`
                                                : `${MIGRATED_COLORS.lightly}`
                                        }
                                    />
                                </FilterButton>
                            </Tooltip>
                            {hasActiveAdvancedFilters && (
                                <ResetFilters
                                    gutterBottom="0"
                                    gutterTop="0"
                                    gutterLeft={isMobile ? '0.5rem' : '0.675rem'}
                                    type="link"
                                    style={{ textWrap: 'nowrap' }}
                                    onClick={onClickResetFilters}
                                >
                                    Reset
                                </ResetFilters>
                            )}
                        </>
                    )}
                </Row>
            )
        );
    }
    // Render the `RIGHT` of the `Toolbar`
    function renderToolbarRight() {
        const { right: Right } = toolbar;
        if (!isEmpty(actionsSchema)) {
            let formattedActionsSchema = _cloneDeep(actionsSchema);
            if (renderCheck('md', 'less')) {
                formattedActionsSchema = formattedActionsSchema.map(button => ({
                    ...button,
                    width: '100%',
                }));
            }

            return (
                <Row gutter={false} justify={renderCheck('md', 'less') ? 'center' : 'flex-end'}>
                    <ToolbarRightButtonGroupStyled
                        buttons={formattedActionsSchema}
                        handlers={actions}
                    />
                </Row>
            );
        }
        // Check if we are overriding the `RIGHT` Component of the `Toolbar`.
        if (isReactComponent(Right)) {
            return <Right />;
        }
        // Carry on if we're not overriding...
        const { options } = sorting;

        if (options) {
            return (
                <Sorting
                    defaultOptions={defaultSortOption}
                    options={options}
                    onChange={handleSortChange}
                    uiSchema={uiSchema}
                />
            );
        }

        return null;
    }

    function renderDataList() {
        const listSchema = {
            headerCellStyle,
            ...list,
            refs: {
                ...refs,
                onChangeSortRef: 'handleSortChange',
            },
            sorting: {
                ...sorting,
                order,
                orderBy,
            },
            selected: selectedSchema,
            dataViewFilters,
        };
        const style = {
            display: view !== 'list' ? 'none' : null,
        };
        const listActions = {
            ...actions,
            handleSortChange,
            handleDataViewFiltersOnClick,
            handleExtraCss,
        };

        if (extraCss && extraCssPropertiesSchema) {
            listSchema.table = {
                style: extraCssPropertiesSchema,
            };
        }
        if (showScroll) {
            listSchema.table = scrollStyles;
        }

        return <DataList style={style} actions={listActions} data={data} schema={listSchema} />;
    }

    function renderDataGrid() {
        const { actions, schema } = props;
        const gridSchema = get(schema, 'grid', {});

        if (_isEmpty(gridSchema)) {
            return null;
        }

        const style = {
            display: view !== 'grid' ? 'none' : null,
        };

        return <DataGrid style={style} actions={actions} data={data} schema={gridSchema} />;
    }

    function renderPagination(placement) {
        const { renderSelectableRef = '' } = selectedSchema;
        const renderSelectable = getHandler(actions, renderSelectableRef);
        // Determine if infoText should render for this pagination placement.
        const infoTextProp = get(pagination, 'infoText', null);
        const infoTextPosition =
            typeof infoTextProp === 'object' ? get(infoTextProp, 'position', 'both') : 'both';
        const shouldShowInfoText = infoTextPosition === 'both' || infoTextPosition === placement;
        // Build the props for the `Pagination`.
        const paginationProps = {
            ...pagination,
            currentPage: page,
            onChangePage: handlePageOnChange,
            onChangeRowsPerPage: handleRowsPerPageOnChange,
            rowsPerPage: rowsPerPage || pagination.rowsPerPage,
            style: {
                padding: '20px 0',
            },
            selected: typeof renderSelectable === 'function' ? selectedSchema : null,
            infoText: shouldShowInfoText ? infoTextProp : null,
        };
        // Render the Pagination.
        return <Pagination {...paginationProps} />;
    }
    // Render the `bottom` `Pagination`.
    function renderBottomPagination() {
        // Only render the Pagination if we're rendering on the `bottom`, or `both` areas.
        return paginationPositionBottom ? renderPagination('bottom') : null;
    }
    // Render the `top` `Pagination`.
    function renderTopPagination() {
        // Only render the Pagination if we're rendering on the `top`, or `both` areas.
        return paginationPositionTop ? renderPagination('top') : null;
    }
    // Render description.
    function renderDescription() {
        return (
            description && (
                <Row justify="center">
                    <Typography fontSize="small" color={descriptionColor}>
                        {description}
                    </Typography>
                </Row>
            )
        );
    }
    // Render the `DataFilters`.
    function renderDataFilters() {
        // Don't render unless we have `filters` available.
        if (!listFiltersAvailable) {
            return null;
        }
        // Extract the passed in filters props from schema.
        const { filters: filtersFromSchema, ...dataFilterProps } = filters;
        // Determine padding for the DataFilters column.
        const colTwoPadding = paginationPositionTop ? '50px 0 0 5px' : '22px 0 0 5px';
        const handlers = {
            ...actions,
            onClearFilters,
            onSelectedFilterRemove,
        };

        // Render the filters.
        return (
            <Col size={{ xs: 12, md: '20%' }}>
                <Container width="100%" padding={colTwoPadding}>
                    <DataFilters {...dataFilterProps} handlers={handlers} filters={dataFilters} />
                </Container>
            </Col>
        );
    }
    // Determine padding for the columns.
    let colThreePadding = !paginationPositionTop ? '50px 5px 0' : padding;
    if (showScroll) {
        colThreePadding = !paginationPositionTop ? '50px 5px 0' : '0';
    }

    if (forceRemovePadding) {
        colThreePadding = '0';
    }

    const colThreeSize = {
        xs: 12,
        md: listFiltersAvailable ? '80%' : 12,
    };
    // Render the `DataView`.
    return (
        <Row
            alignItems="flex-start"
            gutter={false}
            width={width}
            margin={showScroll ? '0px -13px' : ''}
        >
            {dataViewAds}
            {hideToolbar ? null : (
                <Col size={12} gutter={false}>
                    <Toolbar
                        direction={{ xs: 'row', md: 'row' }}
                        left={renderToolbarLeft}
                        center={search && renderToolbarCenter}
                        right={renderToolbarRight}
                        backgroundColor={searchBoxStyleProps}
                        {...toolbarProps}
                    />
                </Col>
            )}
            {!hideDataFilters && renderDataFilters()}
            <Col size={colThreeSize} padding="0">
                <Container width="100%" padding={colThreePadding}>
                    {renderTopPagination()}
                    {renderDescription()}
                    {renderDataList()}
                    {renderDataGrid()}
                    {renderBottomPagination()}
                </Container>
            </Col>
        </Row>
    );
}

DataView.propTypes = {
    /**
     * A map of action handlers used by rendered components.
     */
    actions: PropTypes.object,
    dataViewAds: PropTypes.node,
    /**
     * The main data for the body of the component.
     */
    data: PropTypes.array,
    /**
     * The data to render the data filters part of the component.
     */
    dataFilters: PropTypes.array,
    /**
     * The JSON schema for the `DataView` and its inner components.
     * These include the `DataList` and `DataGrid` components.
     */
    schema: PropTypes.object,
    /**
     * If `true`, the component will update and fire off the `onChange` handler upon mount.
     */
    updateOnMount: PropTypes.bool,
    /**
     * Object used to config component UI
     */
    uiSchema: PropTypes.object,
    /**
     * If `true`, hide data filters.
     */
    hideDataFilters: PropTypes.bool,

    hasModalAdvancedSearch: PropTypes.bool,

    hasSelectAdvancedSearch: PropTypes.bool,
    hasActiveAdvancedFilters: PropTypes.bool,
    resetPagination: PropTypes.bool,
};

DataView.defaultProps = {
    actions: null,
    dataViewAds: null,
    data: null,
    dataFilters: null,
    schema: null,
    uiSchema: {},
    updateOnMount: true,
    hideDataFilters: false,
    hasModalAdvancedSearch: false,
    hasSelectAdvancedSearch: true,
    hasActiveAdvancedFilters: false,
    resetPagination: null,
};

export default React.memo(DataView);
